import { createContext, useCallback, useContext, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { parseSocialIntent, readSocialIntent, saveSocialIntent } from './social-intent';

const SOCIAL_PARAM = 'social';
const SOCIAL_PARAM_VALUE = 'true';
const SOCIAL_HANDOFF_PARAMS = ['state', 'code_challenge', 'callback', 'attempt_id', 'intent'];

export interface SocialModeContextValue {
  disableSocialMode: () => void;
  enableSocialMode: () => void;
  isSocialMode: boolean;
  intent: string | null;
  setIntent: (value: string | null) => void;
  withSocialParam: (path: string) => string;
}

export interface SocialModeProviderProps {
  children: ReactNode;
}

const SocialModeContext = createContext<SocialModeContextValue | null>(null);

function readInitialSocialMode(): boolean {
  if (typeof window === 'undefined') {
    return false;
  }

  return new URLSearchParams(window.location.search).get(SOCIAL_PARAM) === SOCIAL_PARAM_VALUE;
}

function readInitialIntent(): string | null {
  if (typeof window === 'undefined' || !readInitialSocialMode() || !['/login', '/register'].includes(window.location.pathname)) return null;
  const params = new URLSearchParams(window.location.search);
  if (params.has('intent')) {
    const intent = parseSocialIntent(params.get('intent'));
    saveSocialIntent(intent);
    return intent;
  }
  return readSocialIntent();
}

export function SocialModeProvider({ children }: SocialModeProviderProps) {
  const [isSocialMode, setIsSocialMode] = useState(readInitialSocialMode);
  const [intent, updateIntent] = useState(readInitialIntent);

  const setIntent = useCallback((value: string | null) => {
    const valid = parseSocialIntent(value);
    updateIntent(valid);
    saveSocialIntent(valid);
  }, []);

  const enableSocialMode = useCallback(() => {
    setIsSocialMode(true);
  }, []);

  const disableSocialMode = useCallback(() => {
    setIsSocialMode(false);
  }, []);

  const withSocialParam = useCallback(
    (path: string) => {
      if (!isSocialMode) {
        return path;
      }

      const [pathname, query = ''] = path.split('?');
      const params = new URLSearchParams(query);
      params.set(SOCIAL_PARAM, SOCIAL_PARAM_VALUE);
      if (typeof window !== 'undefined') {
        const currentParams = new URLSearchParams(window.location.search);
        SOCIAL_HANDOFF_PARAMS.forEach((key) => {
          if (key === 'intent') return;
          const value = currentParams.get(key);
          if (value) params.set(key, value);
        });
      }
      if (intent) params.set('intent', intent);
      else params.delete('intent');

      return `${pathname}?${params.toString()}`;
    },
    [isSocialMode, intent],
  );

  const value = useMemo(
    () => ({ disableSocialMode, enableSocialMode, isSocialMode, intent, setIntent, withSocialParam }),
    [disableSocialMode, enableSocialMode, isSocialMode, intent, setIntent, withSocialParam],
  );

  return <SocialModeContext.Provider value={value}>{children}</SocialModeContext.Provider>;
}

export function useSocialMode(): SocialModeContextValue {
  const context = useContext(SocialModeContext);

  if (!context) {
    throw new Error('useSocialMode must be used within SocialModeProvider');
  }

  return context;
}

export function useIsSocialMode(): boolean {
  const context = useContext(SocialModeContext);

  return context?.isSocialMode ?? false;
}
